import { useEffect, useState } from 'react';
import { AdminRoleSchema, AdminRole } from '@etiquetachef/types';
import { useAuth } from './AuthProvider';

/**
 * Papel do admin a partir das claims, com a mesma precedência do servidor.
 *
 * Origem: #1078 — 2026-10-29 10:20 +0100 (F2 da #960; o admin não lia papel no cliente).
 *
 * Por quê: espelha `resolveAdminRole` (`apps/functions/src/auth/requireAdminRole.ts`) — `adminRole`
 * válido vence; senão `superAdmin: true` (legado) = ROOT. Divergir faria a tela habilitar um
 * controle que o servidor recusa (ou o contrário).
 *
 * Contrato: puro; `null` = sem papel admin.
 *
 * Usado por: `useAdminRole` e o teste `auth/useAdminRole.test.ts`.
 */
export function adminRoleFromClaims(claims: Record<string, unknown>): AdminRole | null {
  const parsed = AdminRoleSchema.safeParse(claims.adminRole);
  if (parsed.success) return parsed.data;
  return claims.superAdmin === true ? AdminRole.ROOT : null;
}

/**
 * Papel do admin logado: `undefined` enquanto carrega, `null` sem papel.
 *
 * Origem: #1078 — 2026-10-29 10:20 +0100 (F2 da #960).
 *
 * Por quê: a região do país e a chave da Bélgica são só de ROOT; a tela desabilita os controles
 * para os demais papéis em vez de deixar o clique cair num `permission-denied`.
 *
 * Contrato: lê o token já em cache (`getIdTokenResult(false)` — o `AuthProvider` é quem força o
 * refresh, #963) e relê a cada emissão do `onIdTokenChanged` (o `status` do `AuthProvider` é um
 * objeto novo a cada uma). Apresentação: a autorização real é do servidor (`ADMIN_PERMISSIONS`).
 *
 * Falhas conhecidas:
 *  - 2026-10-29 10:57 +0100 (#1078, auditoria F2): o efeito dependia de `status.user`, que é a
 *    MESMA referência entre emissões do token — papel trocado na sessão só aparecia após recarregar;
 *    e o `null` do carregamento mostrava a ROOT o aviso "só ROOT" por um instante.
 *
 * Usado por: `features/countries/RegionsSwitch.tsx`, `features/countries/CountryEditDialog.tsx`.
 */
export function useAdminRole(): AdminRole | null | undefined {
  const { status } = useAuth();
  const [role, setRole] = useState<AdminRole | null | undefined>(undefined);
  useEffect(() => {
    if (status.kind !== 'AUTHORIZED') {
      setRole(null);
      return;
    }
    const user = status.user;
    let alive = true;
    void user.getIdTokenResult(false).then(
      (r) => {
        if (alive) setRole(adminRoleFromClaims(r.claims));
      },
      () => {
        if (alive) setRole(null);
      },
    );
    return (): void => {
      alive = false;
    };
    // `status`, não `status.user`: o user é a mesma referência entre emissões do token (ver Falhas).
  }, [status]);
  return role;
}
